/**
 * Gerador de Gráficos (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_gerador.py).
 *
 * A configuração inteira fica na URL (`cfg`, JSON), menos a tabela colada, que não cabe
 * nela: um link com "dados colados" pede para colar de novo. Como no legado, o gráfico só
 * é gerado no clique em "Gerar gráfico"; os campos editam um rascunho.
 */
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroApi, apiGet, apiPost, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import {
  BarraFiltros,
  CampoData,
  CampoOpcoes,
  GrupoFiltros,
  LARGURA,
} from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarDataHora } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';
import { periodoAtalho } from './ExtracaoDados';

const CAMINHO = '/utilitarios/gerador-de-graficos';

export const TIPOS: { value: string; label: string }[] = [
  { value: 'line', label: 'Linha' },
  { value: 'spline', label: 'Linha suave (spline)' },
  { value: 'area', label: 'Área' },
  { value: 'areaspline', label: 'Área suave' },
  { value: 'column', label: 'Colunas' },
  { value: 'bar', label: 'Barras' },
  { value: 'scatter', label: 'Dispersão' },
  { value: 'pie', label: 'Pizza' },
  { value: 'dual_axis_line', label: 'Dois eixos — linhas' },
  { value: 'dual_axis_line_area', label: 'Dois eixos — linha + área' },
  { value: 'dual_axis_line_column', label: 'Dois eixos — linha + colunas' },
];
const DOIS_EIXOS = ['dual_axis_line', 'dual_axis_line_area', 'dual_axis_line_column'];
const EMPILHAVEIS = ['bar', 'column', 'area', 'areaspline', 'line', 'spline'];

type TipoParam = 'int' | 'float' | 'bool' | 'texto' | 'data' | 'coluna';
interface DefParam {
  nome: string;
  rotulo: string;
  tipo: TipoParam;
  padrao: number | boolean | string | null;
  /** Só aparece quando este parâmetro booleano está marcado. */
  se?: string;
}
const janela = (padrao: number, rotulo = 'Janela (períodos)'): DefParam => ({
  nome: 'window',
  rotulo,
  tipo: 'int',
  padrao,
});
const frequencia = (rotulo = 'Frequência (ex.: MS, QS, D)', padrao = 'MS'): DefParam => ({
  nome: 'frequency',
  rotulo,
  tipo: 'texto',
  padrao,
});
const escalar = (rotulo: string): DefParam => ({
  nome: 'scalar',
  rotulo,
  tipo: 'float',
  padrao: 1,
});
const meses: DefParam = {
  nome: 'period_months',
  rotulo: 'Meses no período',
  tipo: 'int',
  padrao: 1,
};

/** Transformações e parâmetros, com os rótulos e padrões do legado. */
export const TRANSFORMACOES: { value: string; label: string; params: DefParam[] }[] = [
  { value: 'yearly_variation', label: 'Variação anual (YoY)', params: [frequencia()] },
  { value: 'monthly_variation', label: 'Variação mensal (MoM)', params: [frequencia()] },
  { value: 'quarterly_variation', label: 'Variação trimestral (QoQ)', params: [frequencia()] },
  { value: 'monthly_difference', label: 'Diferença mensal', params: [frequencia()] },
  { value: 'moving_average', label: 'Média móvel', params: [janela(21)] },
  { value: 'rolling_max', label: 'Máximo móvel', params: [janela(252)] },
  { value: 'rolling_min', label: 'Mínimo móvel', params: [janela(252)] },
  {
    value: 'rolling_volatility',
    label: 'Volatilidade móvel',
    params: [
      janela(252),
      { nome: 'annualized', rotulo: 'Anualizar', tipo: 'bool', padrao: false },
      {
        nome: 'periods_in_year',
        rotulo: 'Períodos no ano',
        tipo: 'int',
        padrao: 252,
        se: 'annualized',
      },
      { nome: 'calculate_on_returns', rotulo: 'Sobre retornos', tipo: 'bool', padrao: true },
    ],
  },
  {
    value: 'rolling_beta',
    label: 'Beta móvel',
    params: [
      janela(252),
      { nome: 'independent_column', rotulo: 'Contra a série', tipo: 'coluna', padrao: null },
    ],
  },
  { value: 'rolling_sum', label: 'Soma móvel', params: [janela(12)] },
  {
    value: 'cumulative_sum',
    label: 'Soma acumulada',
    params: [frequencia('Frequência (opcional, ex.: MS)', '')],
  },
  {
    value: 'rolling_sum_plus_yearly_variation',
    label: 'Soma móvel + variação anual',
    params: [janela(12, 'Janela da soma (períodos)'), frequencia('Frequência (ex.: MS, QS)')],
  },
  { value: 'multiply', label: 'Multiplicar por escalar', params: [escalar('Multiplicar por')] },
  { value: 'divide', label: 'Dividir por escalar', params: [escalar('Dividir por')] },
  { value: 'subtract', label: 'Subtrair escalar', params: [escalar('Subtrair')] },
  {
    value: 'base_100',
    label: 'Índice base 100',
    params: [
      { nome: 'base_date', rotulo: 'Data base (vazio = 1ª observação)', tipo: 'data', padrao: '' },
    ],
  },
  { value: 'saar', label: 'Taxa anualizada sazonalmente (SAAR)', params: [meses] },
  { value: 'saar_ma', label: 'SAAR com média móvel', params: [meses] },
  { value: 'year_to_date', label: 'Acumulado no ano (YTD)', params: [] },
  { value: 'accumulated_by_year', label: 'Acumulado por ano', params: [] },
];

type Params = Record<string, number | boolean | string | null>;
interface Transformacao {
  coluna: string | null;
  tipo: string;
  params: Params;
}

export interface Config {
  fonte: 'codigos' | 'colado';
  codigos: string[];
  inicio: string;
  fim: string;
  cabecalho: boolean;
  coluna_x: string;
  titulo: string;
  legendas: string;
  titulo_y: string;
  tipo: string;
  empilhamento: string | null;
  casas: number;
  y_max: number | null;
  y_min: number | null;
  altura: number;
  largura: number;
  linha_vertical: { data: string; rotulo: string } | null;
  transformacoes: Transformacao[];
}

export function configPadrao(hoje = new Date()): Config {
  return {
    fonte: 'codigos',
    codigos: [],
    ...periodoAtalho('5A', hoje),
    cabecalho: true,
    coluna_x: '',
    titulo: '',
    legendas: '',
    titulo_y: '',
    tipo: 'line',
    empilhamento: null,
    casas: 2,
    y_max: null,
    y_min: null,
    altura: 500,
    largura: 1200,
    linha_vertical: null,
    transformacoes: [],
  };
}

/** `cfg` da URL; inválido ou vazio = nada gerado ainda. */
export function lerConfig(texto: string): Config | null {
  if (!texto) return null;
  try {
    return { ...configPadrao(), ...(JSON.parse(texto) as Partial<Config>) };
  } catch {
    return null;
  }
}

function paramsPadrao(tipo: string, params: Params = {}): Params {
  const def = TRANSFORMACOES.find((t) => t.value === tipo);
  const saida: Params = { keep_original_data: params.keep_original_data ?? false };
  for (const p of def?.params ?? []) saida[p.nome] = params[p.nome] ?? p.padrao;
  return saida;
}

interface DadosGrafico {
  grafico: GraficoApi;
  series: number;
  observacoes: number;
  colunas: string[];
}
interface DadosPrevia {
  linhas: number;
  series: string[];
  tabela: TabelaApi;
}

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

function corpoDaConfig(c: Config, texto: string) {
  const { cabecalho, coluna_x, ...resto } = c;
  return {
    ...resto,
    inicio: c.inicio || null,
    fim: c.fim || null,
    colado: { texto: c.fonte === 'colado' ? texto : '', cabecalho, coluna_x },
    empilhamento: EMPILHAVEIS.includes(c.tipo) ? c.empilhamento : null,
  };
}

function EditorTransformacao({
  t,
  i,
  colunas,
  mudar,
  remover,
}: {
  t: Transformacao;
  i: number;
  colunas: string[];
  mudar: (t: Transformacao) => void;
  remover: () => void;
}) {
  const def = TRANSFORMACOES.find((d) => d.value === t.tipo);
  const param = (nome: string, valor: Params[string]) =>
    mudar({ ...t, params: { ...t.params, [nome]: valor } });
  return (
    <Paper withBorder p="xs" radius="sm">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <Text size="sm" fw={600} w={24} mb={8}>
          #{i + 1}
        </Text>
        <Select
          label="Série"
          data={colunas}
          value={t.coluna}
          onChange={(coluna) => mudar({ ...t, coluna })}
          placeholder={colunas.length ? 'Escolha' : 'Escolha as séries antes'}
          w={LARGURA.selecao}
          searchable
        />
        <Select
          label="Transformação"
          data={TRANSFORMACOES}
          value={t.tipo}
          onChange={(tipo) => tipo && mudar({ ...t, tipo, params: paramsPadrao(tipo, t.params) })}
          w={260}
          searchable
        />
        {def?.params
          .filter((p) => !p.se || t.params[p.se])
          .map((p) => {
            const valor = t.params[p.nome];
            if (p.tipo === 'bool')
              return (
                <Checkbox
                  key={p.nome}
                  label={p.rotulo}
                  checked={!!valor}
                  onChange={(e) => param(p.nome, e.currentTarget.checked)}
                  mb={8}
                />
              );
            if (p.tipo === 'coluna')
              return (
                <Select
                  key={p.nome}
                  label={p.rotulo}
                  data={colunas.filter((c) => c !== t.coluna)}
                  value={(valor as string | null) ?? null}
                  onChange={(v) => param(p.nome, v)}
                  w={LARGURA.selecao}
                />
              );
            if (p.tipo === 'data')
              return (
                <TextInput
                  key={p.nome}
                  label={p.rotulo}
                  type="date"
                  value={(valor as string) ?? ''}
                  onChange={(e) => param(p.nome, e.currentTarget.value)}
                  w={LARGURA.data + 60}
                />
              );
            if (p.tipo === 'texto')
              return (
                <TextInput
                  key={p.nome}
                  label={p.rotulo}
                  value={(valor as string) ?? ''}
                  onChange={(e) => param(p.nome, e.currentTarget.value)}
                  w={LARGURA.texto}
                />
              );
            return (
              <NumberInput
                key={p.nome}
                label={p.rotulo}
                value={valor as number}
                onChange={(v) => param(p.nome, v === '' ? p.padrao : Number(v))}
                min={p.tipo === 'int' ? 1 : undefined}
                allowDecimal={p.tipo === 'float'}
                decimalSeparator=","
                w={LARGURA.numero + 30}
              />
            );
          })}
        <Checkbox
          label="Manter série original"
          checked={!!t.params.keep_original_data}
          onChange={(e) => param('keep_original_data', e.currentTarget.checked)}
          mb={8}
        />
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={remover}
          aria-label={`Remover transformação ${i + 1}`}
          mb={6}
        >
          ✕
        </ActionIcon>
      </Group>
    </Paper>
  );
}

/** Rascunho da configuração; recriado (key) quando a URL muda. */
function Formulario({
  inicial,
  texto,
  setTexto,
  codigos,
  maxSeries,
  carregando,
  aoGerar,
}: {
  inicial: Config;
  texto: string;
  setTexto: (t: string) => void;
  codigos: string[];
  maxSeries: number;
  carregando: boolean;
  aoGerar: (c: Config) => void;
}) {
  const [c, setC] = useState(inicial);
  const mudar = (m: Partial<Config>) => setC((atual) => ({ ...atual, ...m }));
  const colado = c.fonte === 'colado';

  const [previaChave] = useDebouncedValue(
    { texto, cabecalho: c.cabecalho, coluna_x: c.coluna_x },
    400,
  );
  const previa = useQuery({
    queryKey: ['utilitarios.gerador.previa', previaChave],
    queryFn: ({ signal }) =>
      apiPost<Envelope<DadosPrevia>>(`${CAMINHO}/previa`, previaChave, signal),
    enabled: colado && !!previaChave.texto.trim(),
    placeholderData: keepPreviousData,
  });

  const colunas = colado ? (previa.data?.data.series ?? []) : c.codigos;
  const t = c.transformacoes;
  const setT = (transformacoes: Transformacao[]) => mudar({ transformacoes });
  const prontos = colado ? !!texto.trim() : c.codigos.length > 0;

  return (
    <>
      <BarraFiltros carregando={carregando || previa.isFetching}>
        <GrupoFiltros titulo="Dados">
          <CampoOpcoes
            rotulo="Fonte"
            valor={c.fonte}
            aoMudar={(f) => mudar({ fonte: f as Config['fonte'], transformacoes: [] })}
            opcoes={[
              { valor: 'codigos', rotulo: 'Séries do banco' },
              { valor: 'colado', rotulo: 'Colar dados' },
            ]}
          />
          {colado ? (
            <>
              <Textarea
                label="Dados (Excel, CSV ou TSV)"
                description="A primeira coluna é o eixo X"
                placeholder="Cole aqui com Ctrl+V"
                value={texto}
                onChange={(e) => setTexto(e.currentTarget.value)}
                autosize
                minRows={3}
                maxRows={8}
                w={340}
              />
              <Stack gap="xs" mt={4}>
                <Checkbox
                  label="Primeira linha é cabeçalho"
                  checked={c.cabecalho}
                  onChange={(e) => mudar({ cabecalho: e.currentTarget.checked })}
                />
                <TextInput
                  label="Coluna do eixo X"
                  description="Opcional: nome, ou 0 para a primeira"
                  value={c.coluna_x}
                  onChange={(e) => mudar({ coluna_x: e.currentTarget.value })}
                  w={LARGURA.texto}
                />
              </Stack>
            </>
          ) : (
            <>
              <MultiSelect
                label="Séries"
                // description={`Até ${maxSeries}`}
                placeholder="Digite para buscar um código"
                searchable
                clearable
                limit={100}
                maxValues={maxSeries}
                data={codigos}
                value={c.codigos}
                onChange={(codigos) => mudar({ codigos })}
                w={320}
              />
              <CampoData rotulo="Início" valor={c.inicio} aoMudar={(d) => mudar({ inicio: d })} />
              <CampoData rotulo="Fim" valor={c.fim} aoMudar={(d) => mudar({ fim: d })} />
              <Group gap={4} mt={25}>
                {['1A', '3A', '5A', '10A', 'YTD', 'Máx'].map((a) => (
                  <Button
                    key={a}
                    size="compact-sm"
                    variant="default"
                    onClick={() => mudar(periodoAtalho(a))}
                  >
                    {a}
                  </Button>
                ))}
              </Group>
            </>
          )}
        </GrupoFiltros>
        <GrupoFiltros titulo="Aparência">
          <TextInput
            label="Título"
            placeholder="Ex.: IPCA acumulado 12 meses"
            value={c.titulo}
            onChange={(e) => mudar({ titulo: e.currentTarget.value })}
            w={LARGURA.selecao}
          />
          <Select
            label="Tipo"
            data={TIPOS}
            value={c.tipo}
            onChange={(tipo) => tipo && mudar({ tipo })}
            w={LARGURA.selecao}
          />
          {EMPILHAVEIS.includes(c.tipo) && (
            <Select
              label="Empilhamento"
              data={[
                { value: 'nenhum', label: 'Nenhum' },
                { value: 'normal', label: 'Empilhado' },
                { value: 'percent', label: 'Empilhado (%)' },
              ]}
              value={c.empilhamento ?? 'nenhum'}
              onChange={(v) => mudar({ empilhamento: !v || v === 'nenhum' ? null : v })}
              w={160}
            />
          )}
          <TextInput
            label="Nomes na legenda"
            // description="Opcional; um por série, com ;"
            placeholder="Série A;Série B"
            value={c.legendas}
            onChange={(e) => mudar({ legendas: e.currentTarget.value })}
            w={LARGURA.selecao}
          />
          <TextInput
            label="Título do eixo Y"
            description={DOIS_EIXOS.includes(c.tipo) ? 'Esquerdo;direito' : ' '}
            placeholder="Ex.: Valor (%)"
            value={c.titulo_y}
            onChange={(e) => mudar({ titulo_y: e.currentTarget.value })}
            w={LARGURA.texto}
          />
          <NumberInput
            label="Casas decimais"
            description=" "
            value={c.casas}
            onChange={(v) => mudar({ casas: v === '' ? 2 : Number(v) })}
            min={0}
            max={10}
            w={LARGURA.numero}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Eixo Y e tamanho">
          <NumberInput
            label="Mínimo"
            placeholder="Automático"
            value={c.y_min ?? ''}
            onChange={(v) => mudar({ y_min: v === '' ? null : Number(v) })}
            decimalSeparator=","
            w={LARGURA.numero}
          />
          <NumberInput
            label="Máximo"
            placeholder="Automático"
            value={c.y_max ?? ''}
            onChange={(v) => mudar({ y_max: v === '' ? null : Number(v) })}
            decimalSeparator=","
            w={LARGURA.numero}
          />
          <CampoOpcoes
            rotulo="Disposição"
            valor={c.largura <= 600 ? 'lado' : 'linha'}
            aoMudar={(v) => mudar({ largura: v === 'lado' ? 600 : 1200, altura: 500 })}
            opcoes={[
              { valor: 'linha', rotulo: 'Linha completa' },
              { valor: 'lado', rotulo: 'Lado a lado' },
            ]}
          />
          <NumberInput
            label="Altura (px)"
            value={c.altura}
            onChange={(v) => mudar({ altura: v === '' ? 500 : Number(v) })}
            min={200}
            max={1200}
            step={50}
            w={LARGURA.numero}
          />
          <NumberInput
            label="Largura (px)"
            value={c.largura}
            onChange={(v) => mudar({ largura: v === '' ? 1200 : Number(v) })}
            min={200}
            max={1200}
            step={50}
            w={LARGURA.numero}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Linha vertical">
          <TextInput
            label="Data"
            type="date"
            value={c.linha_vertical?.data ?? ''}
            onChange={(e) =>
              mudar({
                linha_vertical: e.currentTarget.value
                  ? { data: e.currentTarget.value, rotulo: c.linha_vertical?.rotulo ?? '' }
                  : null,
              })
            }
            w={LARGURA.data}
          />
          <TextInput
            label="Rótulo"
            placeholder="Ex.: Marco regulatório"
            disabled={!c.linha_vertical}
            value={c.linha_vertical?.rotulo ?? ''}
            onChange={(e) =>
              c.linha_vertical &&
              mudar({ linha_vertical: { ...c.linha_vertical, rotulo: e.currentTarget.value } })
            }
            w={LARGURA.texto}
          />
        </GrupoFiltros>
      </BarraFiltros>

      <Secao
        titulo="Transformações"
        legenda="Aplicadas na ordem, antes do gráfico. A série original sai do gráfico, a menos que seja mantida."
        acoes={
          <Group gap="xs">
            {t.length > 0 && (
              <Button variant="subtle" color="gray" onClick={() => setT([])}>
                Limpar
              </Button>
            )}
            <Button
              variant="light"
              onClick={() =>
                setT([
                  ...t,
                  {
                    coluna: colunas[0] ?? null,
                    tipo: 'moving_average',
                    params: paramsPadrao('moving_average'),
                  },
                ])
              }
            >
              Adicionar transformação
            </Button>
          </Group>
        }
      >
        {t.length === 0 ? (
          <Text size="sm" c="dimmed">
            Nenhuma transformação.
          </Text>
        ) : (
          <Stack gap="xs">
            {t.map((item, i) => (
              <EditorTransformacao
                key={i}
                t={item}
                i={i}
                colunas={colunas}
                mudar={(novo) => setT(t.map((x, j) => (j === i ? novo : x)))}
                remover={() => setT(t.filter((_, j) => j !== i))}
              />
            ))}
          </Stack>
        )}
        <Group justify="flex-end" mt="md">
          {!colado && c.inicio > c.fim && (
            <Text size="sm" c="red">
              A data de início não pode ser posterior à data de fim.
            </Text>
          )}
          <Button
            color="dourado"
            disabled={!prontos || (!colado && c.inicio > c.fim)}
            onClick={() => aoGerar(c)}
          >
            Gerar gráfico
          </Button>
        </Group>
      </Secao>

      {colado && previa.isError && (
        <Alert color="red">
          {mensagem(previa.error, 'Não foi possível ler os dados colados.')}
        </Alert>
      )}
      {colado && previa.data && !previa.isError && (
        <Secao
          titulo="Pré-visualização dos dados colados"
          legenda={`${previa.data.data.linhas.toLocaleString('pt-BR')} linhas · ${previa.data.data.series.length} série(s): ${previa.data.data.series.slice(0, 5).join(', ')}${previa.data.data.series.length > 5 ? '…' : ''}. Primeiras 20 linhas.`}
        >
          <AvisosApi avisos={[previa.data.meta.avisos]} />
          <TabelaDados tabela={previa.data.data.tabela} alturaAutomatica />
        </Secao>
      )}
    </>
  );
}

export function GeradorGraficos() {
  const [filtros, definir] = useFiltrosUrl({ cfg: '' });
  const config = lerConfig(filtros.cfg);
  const [texto, setTexto] = useState('');
  const [textoEnviado, setTextoEnviado] = useState('');

  const codigos = useQuery({
    queryKey: ['utilitarios.gerador.codigos'],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ codigos: string[]; max_series: number }>>(`${CAMINHO}/codigos`, {}, signal),
    staleTime: 60 * 60 * 1000,
  });

  const faltaColar = config?.fonte === 'colado' && !textoEnviado.trim();
  const corpo = config ? corpoDaConfig(config, textoEnviado) : null;
  const grafico = useQuery({
    queryKey: ['utilitarios.gerador', corpo],
    queryFn: ({ signal }) => apiPost<Envelope<DadosGrafico>>(CAMINHO, corpo, signal),
    enabled: !!config && !faltaColar,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
  const dados = grafico.data?.data;

  const gerar = (c: Config) => {
    setTextoEnviado(texto);
    definir({ cfg: JSON.stringify(c) });
  };

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Gerador de Gráficos"
        descricao="Gráficos a partir de séries do banco ou de uma tabela colada do Excel, com transformações, dois eixos e linha vertical."
        dadosAte={grafico.data?.meta.dados_ate}
        fonte="Banco Persevera (indicadores) ou dados colados"
      />
      <Formulario
        key={filtros.cfg}
        inicial={config ?? configPadrao()}
        texto={texto}
        setTexto={setTexto}
        codigos={codigos.data?.data.codigos ?? []}
        maxSeries={codigos.data?.data.max_series ?? 30}
        carregando={codigos.isFetching || grafico.isFetching}
        aoGerar={gerar}
      />
      {codigos.isError && (
        <Alert color="red">
          {mensagem(codigos.error, 'Não foi possível carregar os códigos.')}
        </Alert>
      )}
      {!config && (
        <Alert color="blue" title="Como usar">
          1. Escolha a fonte: séries do banco ou uma tabela colada do Excel. 2. Ajuste a aparência
          e, se quiser, as transformações. 3. Clique em <b>Gerar gráfico</b>. Duplo clique no
          gráfico abre em tela cheia; o menu ⋮ do gráfico exporta PNG/SVG.
        </Alert>
      )}
      {faltaColar && (
        <Alert color="blue">
          Este link tem a configuração do gráfico, mas não os dados colados (eles não ficam na URL).
          Cole a tabela de novo e clique em <b>Gerar gráfico</b>.
        </Alert>
      )}
      {grafico.isError && (
        <Alert
          color={
            grafico.error instanceof ErroApi && grafico.error.status === 404 ? 'yellow' : 'red'
          }
        >
          {mensagem(grafico.error, 'Não foi possível gerar o gráfico.')}
        </Alert>
      )}
      {config && !faltaColar && grafico.isPending && <Loader />}
      {dados && !grafico.isError && !faltaColar && (
        <Secao
          titulo={config?.titulo || 'Gráfico'}
          legenda={`Gerado em ${formatarDataHora(grafico.data?.meta.gerado_em ?? null)} · ${dados.series} série(s) · ${dados.observacoes.toLocaleString('pt-BR')} observações · duplo clique para tela cheia; o menu ⋮ exporta PNG/SVG.`}
        >
          <AvisosApi avisos={[grafico.data?.meta.avisos]} />
          <div style={{ overflowX: 'auto' }}>
            {/* Recria o gráfico a cada resultado: a estrutura (eixos, nº de séries) muda entre
                gerações e a atualização no lugar do Highcharts deixava séries órfãs. */}
            <Grafico key={grafico.dataUpdatedAt} grafico={dados.grafico} />
          </div>
        </Secao>
      )}
    </Pagina>
  );
}
